import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { pendingChanges, serialQueue, writeDayRow } from '../lib/dayRowCas';
import { refreshTodayOnFocus } from '../lib/dayLogFocusRefresh';
import { onSignOut } from '../lib/signOutReset';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import {
  type HydrationDay,
  type HydrationEntry,
  type HydrationUnit,
  DEFAULT_TARGET_ML,
  clampTarget,
  sumHydrationXP,
  hydrationStreak,
  remainingMl,
  hydrationProgress,
  toLocalYMD,
  shiftYMD,
} from '../utils/hydration';
import { useHabitStore } from './habitStore';

// How many trailing days to load for streak/trend/XP.
const HISTORY_DAYS = 60;

interface HydrationRow {
  id?: string;
  log_date: string;
  intake_ml: number;
  target_ml: number;
  entries: HydrationEntry[] | null;
  updated_at: string;
}

/** One day's values, as the server holds them (or as this tab shows them). */
interface DayValues {
  intakeMl: number;
  targetMl: number;
  entries: HydrationEntry[];
}

function rowToDay(row: HydrationRow): HydrationDay {
  return { date: row.log_date, intakeMl: row.intake_ml ?? 0, targetMl: row.target_ml ?? DEFAULT_TARGET_ML };
}

interface HydrationStore {
  // today's live row
  todayDate: string;
  todayIntakeMl: number;
  todayTargetMl: number;
  entries: HydrationEntry[];
  // recent days (including today) for streak / trend / XP
  history: HydrationDay[];
  // persistent settings
  targetMl: number;
  unit: HydrationUnit;

  isLoading: boolean;
  isInitialized: boolean;
  initializedUserId: string | null;

  /**
   * ⭐ GROWTH FOUNDATIONS EVIDENCE (2026-10-05). The checklist must count only
   * what was really saved, so the store now keeps two facts it used to discard:
   *  · `targetSaved` — `user_preferences.hydration_target_ml` is NOT null (the
   *    store shows 2,500 when it is; the default is not a deliberate choice).
   *    True after a confirmed `setTarget`, never from an optimistic one.
   *  · `everLogged` — any `hydration_logs` row with intake > 0, EVER (not just
   *    the 60 loaded days). `null` = not known yet.
   * ⚠️ A log row is no proof of a target: `logDrink` snapshots the target into
   * `target_ml` (NOT NULL DEFAULT 2500) on every drink.
   */
  targetSaved: boolean;
  everLogged: boolean | null;
  /** The settings or history read failed — "unknown", never "nothing yet". */
  loadFailed: boolean;

  initialize: () => Promise<void>;
  /**
   * Re-read today's row (the tab came back — lib/dayLogFocusRefresh) and show it with
   * this tab's unsaved changes on top. ⚠️ Read only: it never writes the row, and it does
   * NOT re-sync linked habits (that would write the habit, and can mint its XP).
   */
  refreshToday: () => Promise<void>;
  /**
   * ⭐ Each writer resolves `true` only when the database accepted it. On a
   * failure the optimistic local change is ROLLED BACK (and linked habits
   * re-synced), so the screen never shows a drink or a goal that was not saved.
   */
  logDrink: (ml: number) => Promise<boolean>;
  undoLast: () => Promise<boolean>;
  setTarget: (ml: number) => Promise<boolean>;
  setUnit: (unit: HydrationUnit) => void;

  getHydrationXP: () => number;
  getStreak: () => number;
  getRemainingMl: () => number;
  getProgress: () => number;
  clearStore: () => void;
}

/** Merge today's live values into the history array (insert if missing). */
function withTodayInHistory(history: HydrationDay[], today: HydrationDay): HydrationDay[] {
  const others = history.filter(d => d.date !== today.date);
  return [...others, today].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * ⚠️⚠️ A DRINK IS A CHANGE, NEVER THE DAY (docs/DAY_LOG_STALE_WRITES.md). Today is one
 * row; every writer used to upsert it whole from this tab's memory — so a stale tab
 * (or one left open overnight: `rolledOver ? []`, or one still loading) brought back a
 * drink undone elsewhere, dropped drinks logged elsewhere, and `setTarget` rewrote the
 * stale entries and intake. Now each write reads today's row, applies its ONE change
 * (add this drink / remove that drink / set the goal) and writes with compare-and-swap.
 *
 * The screen shows the server's day with this tab's unsaved changes on top (`pending`),
 * so a tap shows at once, a refused write takes back only its own change, and a stale
 * tab catches up on today when it writes — or when it comes back (`refreshToday`).
 */
let enqueue = serialQueue();
let loading: Promise<void> | null = null;
const pending = pendingChanges<DayValues>();
/** The server's copy of the day this tab last read or wrote. */
let server: { date: string; day: DayValues } | null = null;
/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a drink notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store — and a write still
 * waiting its turn is never sent once that account has left.
 */
let epoch = 0;
const focus = refreshTodayOnFocus(() => useHydrationStore.getState().refreshToday());

const COLUMNS = 'log_date, intake_ml, target_ml, entries';

function rowValues(row: HydrationRow | null, fallbackTarget: number): DayValues {
  if (!row) return { intakeMl: 0, targetMl: fallbackTarget, entries: [] };
  return {
    intakeMl: row.intake_ml ?? 0,
    targetMl: row.target_ml ?? fallbackTarget,
    entries: Array.isArray(row.entries) ? row.entries : [],
  };
}

/** The columns a change touched — all of them for a new row (the DB default goal is not the user's). */
function changedColumns(before: DayValues, after: DayValues, isNew: boolean): Record<string, unknown> | null {
  if (after === before) return null;
  const out: Record<string, unknown> = {};
  if (isNew || after.intakeMl !== before.intakeMl) out.intake_ml = after.intakeMl;
  if (isNew || after.targetMl !== before.targetMl) out.target_ml = after.targetMl;
  if (isNew || after.entries !== before.entries) out.entries = after.entries;
  return Object.keys(out).length ? out : null;
}

export const useHydrationStore = create<HydrationStore>((set, get) => {
  /**
   * Show `date` as the server has it plus this tab's unsaved changes; re-sync linked
   * habits from the CONFIRMED day — except where `habits: false` (a tap not saved yet,
   * a focus refresh).
   */
  const show = (date: string, opts: { habits?: boolean } = {}) => {
    const base = server?.date === date ? server.day : rowValues(null, get().targetMl);
    const day = pending.over(date, base);
    set({
      todayDate: date,
      todayIntakeMl: day.intakeMl,
      todayTargetMl: day.targetMl,
      entries: day.entries,
      history: withTodayInHistory(get().history, { date, intakeMl: day.intakeMl, targetMl: day.targetMl }),
    });
    // Completion follows the tracker (complete at ≥ target, open below), decided on the
    // habit's cloud row (habitStore, os#125) — so calling it again changes nothing.
    // ⚠️ From the SERVER's day only, never a drink still on its way: a day's mint is never
    // revoked, so a habit ticked by a drink the database then refused kept its XP.
    // ⚠️ Not on a refresh: this tab's habit copy can be as stale as its day was.
    // The next drink logged here re-syncs it, as before.
    if (opts.habits !== false && server?.date === date) {
      void useHabitStore.getState().syncHydrationHabits(server.day.intakeMl, server.day.targetMl);
    }
    return day;
  };

  /**
   * Save one change to `date`'s row. It is already on screen (added to `pending` by the
   * caller); this writes it onto the SERVER's row and settles it either way.
   */
  const save = (userId: string, date: string, op: ReturnType<typeof pending.add>, at: number) =>
    enqueue(async () => {
      if (at !== epoch) return false; // signed out while it waited its turn: never sent
      const fallback = get().targetMl;
      const res = await writeDayRow<HydrationRow>(
        { table: 'hydration_logs', userId, date, columns: COLUMNS },
        (row) => {
          const before = rowValues(row, fallback);
          return changedColumns(before, op.apply(before), !row);
        },
      );
      if (at !== epoch) return res.ok; // signed out while it saved: not shown to the next account
      pending.settle(op);
      if (res.ok) server = { date, day: rowValues(res.row, fallback) };
      else console.error('Error saving hydration log:', res.error);
      show(date);
      return res.ok;
    });

  const load = async (): Promise<void> => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== epoch) return;
    if (get().initializedUserId === user.id) return;

    set({ isLoading: true });
    try {
      const prefs = await fetchUserPreferences();
      const prefsFailed = !prefs || Boolean(prefs.failed);
      const targetSaved = prefs?.row?.hydration_target_ml != null;
      const target = clampTarget(
        prefs?.row?.hydration_target_ml != null ? Number(prefs.row.hydration_target_ml) : DEFAULT_TARGET_ML
      );
      const unit: HydrationUnit = prefs?.row?.hydration_unit === 'oz' ? 'oz' : 'ml';

      const todayStr = toLocalYMD(new Date());
      const since = shiftYMD(todayStr, -HISTORY_DAYS);

      const { data, error } = await supabase
        .from('hydration_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', since)
        .order('log_date', { ascending: true });
      if (error) throw error;

      const rows = (data ?? []) as HydrationRow[];
      const history = rows.map(rowToDay);
      const todayRow = rows.find(r => r.log_date === todayStr);

      // ⭐ "Ever logged a drink" — the loaded 60 days answer it most of the time;
      // otherwise ONE row, older than the window, settles it.
      let everLogged: boolean | null = rows.some(r => (r.intake_ml ?? 0) > 0) ? true : null;
      let olderFailed = false;
      if (everLogged === null) {
        const older = await supabase
          .from('hydration_logs')
          .select('log_date')
          .eq('user_id', user.id)
          .gt('intake_ml', 0)
          .limit(1);
        if (older.error) { olderFailed = true; console.error('Error checking hydration history:', older.error); }
        else everLogged = (older.data ?? []).length > 0;
      }
      // Signed out while reading: the next account must not get this one's drinks.
      if (at !== epoch) return;
      server = { date: todayStr, day: rowValues(todayRow ?? null, target) };

      set({
        targetMl: target,
        unit,
        history,
        isLoading: false,
        isInitialized: true,
        initializedUserId: user.id,
        targetSaved,
        everLogged,
        loadFailed: prefsFailed || olderFailed,
      });
      // Today on screen (with anything tapped during the load on top), and linked
      // habits reconciled with it (heals any habit left locked below target by older behavior).
      show(todayStr);
      focus.read(todayStr);
    } catch (err) {
      if (at !== epoch) return; // the next account's load may be running: leave its flags alone
      console.error('Error initializing hydration:', err);
      set({ isLoading: false, loadFailed: true });
    }
  };

  /**
   * ⚠️ Read only, and in the write queue: a drink in flight lands first (and is read back);
   * a drink tapped meanwhile stays on screen (`pending`) until its own write answers.
   */
  const refreshToday = () => enqueue(async () => {
    const { user } = await syncManager.safeAuthCheck();
    if (!user || get().initializedUserId !== user.id) return;
    // The calendar's today — a tab woken after midnight moves on to the new day.
    const date = toLocalYMD(new Date());
    const { data, error } = await supabase
      .from('hydration_logs')
      .select(`${COLUMNS}, updated_at`)
      .eq('user_id', user.id)
      .eq('log_date', date)
      .maybeSingle();
    if (error) { console.error('Error refreshing hydration:', error); return; }
    if (get().initializedUserId !== user.id) return;        // signed out while it read
    server = { date, day: rowValues((data ?? null) as HydrationRow | null, get().targetMl) };
    if (server.day.intakeMl > 0) set({ everLogged: true });
    show(date, { habits: false });
    focus.read(date);
  });

  return {
    todayDate: toLocalYMD(new Date()),
    todayIntakeMl: 0,
    todayTargetMl: DEFAULT_TARGET_ML,
    entries: [],
    history: [],
    targetMl: DEFAULT_TARGET_ML,
    unit: 'ml',

    isLoading: false,
    isInitialized: false,
    initializedUserId: null,
    targetSaved: false,
    everLogged: null,
    loadFailed: false,

    /* ⚠️ A call while loading JOINS that load — it used to return at once, so a caller
       that awaited it (HydrationLogPromptHost) logged onto a day that wasn't loaded yet. */
    initialize: () => {
      if (loading) return loading;
      // ⚠️ Clears only its own: a sign-out drops this load and the next account starts its own.
      const mine: Promise<void> = enqueue(load).finally(() => { if (loading === mine) loading = null; });
      loading = mine;
      return mine;
    },

    refreshToday,

    logDrink: async (ml: number) => {
      const amount = Math.round(ml);
      if (!amount || amount <= 0) return false;
      const at = epoch;
      const { user } = await syncManager.safeAuthCheck();
      if (!user || at !== epoch) return false;

      // Today is the calendar's today — never the day this tab loaded (a tab left open
      // overnight starts from the server's today, not from an empty one).
      const date = toLocalYMD(new Date());
      const drink: HydrationEntry = { ml: amount, at: new Date().toISOString() };
      const op = pending.add(date, d => ({ ...d, intakeMl: d.intakeMl + amount, entries: [...d.entries, drink] }));
      show(date, { habits: false });
      const ok = await save(user.id, date, op, at);
      if (ok && at === epoch) set({ everLogged: true });
      return ok;
    },

    undoLast: async () => {
      const at = epoch;
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return false;
      const { entries, todayDate: date } = get();
      const drink = entries[entries.length - 1];
      if (!drink) return false;

      // ⚠️ Undo removes the drink THIS screen shows last — matched by its time and amount
      // on the server's day. Already gone (undone elsewhere) → nothing to remove, and
      // nothing else is touched (the server's last drink may be another device's).
      const op = pending.add(date, d => {
        let i = -1;
        for (let k = d.entries.length - 1; k >= 0; k--) {
          if (d.entries[k].at === drink.at && d.entries[k].ml === drink.ml) { i = k; break; }
        }
        if (i < 0) return d;
        return { ...d, intakeMl: Math.max(0, d.intakeMl - drink.ml), entries: [...d.entries.slice(0, i), ...d.entries.slice(i + 1)] };
      });
      show(date, { habits: false });
      return save(user.id, date, op, at);
    },

    setTarget: async (ml: number) => {
      const at = epoch;
      const target = clampTarget(ml);
      const date = toLocalYMD(new Date());
      const settingBefore = get().targetMl;
      // The default setting AND today's goal both move (today hasn't "closed" yet).
      set({ targetMl: target });
      const op = pending.add(date, d => (d.targetMl === target ? d : { ...d, targetMl: target }));
      show(date, { habits: false });

      /* ⚠️⚠️ THE SETTING IS THE PROOF. Only a confirmed preference write marks the
         target as deliberately saved; a refused one rolls the screen back. */
      const prefsSaved = await saveUserPreference({ hydration_target_ml: target });
      // Signed out meanwhile: neither "saved" nor the old goal goes into the next account's store.
      if (at !== epoch) return prefsSaved;
      if (!prefsSaved) {
        set({ targetMl: settingBefore });
        pending.settle(op);
        show(date);
        return false;
      }
      set({ targetSaved: true });

      const { user } = await syncManager.safeAuthCheck();
      if (!user) { pending.settle(op); show(date); return true; }
      // ⚠️ Today's goal ONLY — never this tab's copy of the drinks. A refusal here is not a
      // failed save (the SETTING above was saved): today's row keeps its old goal, and
      // the screen shows what the row holds.
      await save(user.id, date, op, at);
      return true;
    },

    setUnit: (unit: HydrationUnit) => {
      set({ unit });
      void saveUserPreference({ hydration_unit: unit });
    },

    getHydrationXP: () => sumHydrationXP(get().history),
    getStreak: () => hydrationStreak(get().history, get().todayDate),
    getRemainingMl: () => remainingMl(get().todayIntakeMl, get().todayTargetMl),
    getProgress: () => hydrationProgress(get().todayIntakeMl, get().todayTargetMl),

    /**
     * Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does
     * not reload the page: without this the drinks, goal and history stayed in memory for
     * the next account. The module's copies go too: the server's day, unsaved drinks, the
     * work queue and the load in flight, the focus refresh.
     */
    clearStore: () => {
      epoch += 1;
      // A fresh queue: the next account's load never waits behind the old account's work.
      enqueue = serialQueue();
      loading = null;
      server = null;
      pending.clear();
      focus.forget();
      set({
        todayDate: toLocalYMD(new Date()),
        todayIntakeMl: 0,
        todayTargetMl: DEFAULT_TARGET_ML,
        entries: [],
        history: [],
        targetMl: DEFAULT_TARGET_ML,
        unit: 'ml',
        isLoading: false,
        isInitialized: false,
        initializedUserId: null,
        targetSaved: false,
        everLogged: null,
        loadFailed: false,
      });
    },
  };
});

onSignOut(() => useHydrationStore.getState().clearStore());
